// skipcq: JS-W1028
import { useEffect, useState, useCallback } from 'react';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { db } from '../utils/firebase';
import { METRIC_DEFINITIONS_COLLECTION } from '../utils/firestoreCollections';
import {
  METRIC_APP_MAPPING_DRAUGHT_INSIGHTS,
  validateMetricKey,
} from '../domain/metricConstants';

function sortMetrics(rows) {
  return [...rows].sort((a, b) => {
    const sortOrderDiff =
      Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0);
    if (sortOrderDiff !== 0) return sortOrderDiff;
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });
}

function normalizeAppMapping(value) {
  const trimmedMapping = (value ?? '').trim();
  if (!trimmedMapping) return '';
  if (trimmedMapping === METRIC_APP_MAPPING_DRAUGHT_INSIGHTS) {
    return METRIC_APP_MAPPING_DRAUGHT_INSIGHTS;
  }
  if (import.meta.env.DEV && trimmedMapping) {
    // eslint-disable-next-line no-console
    console.warn(
      '[useMetricDefinitionsList] Unknown appMapping value; normalizing to empty string:',
      trimmedMapping,
    );
  }
  return '';
}

/**
 * Shared validation and normalization for create/update metric payloads.
 * @param {{ name: unknown, image: unknown, helpText: unknown, appMapping: unknown, sortOrder: unknown }} fields
 */
function validateAndNormalizeFields({ name, image, helpText, appMapping, sortOrder }) {
  const nameTrimmed = (name ?? '').trim();
  if (!nameTrimmed) {
    throw new Error('Name is required.');
  }

  const imageTrimmed = (image ?? '').trim();
  if (!imageTrimmed) {
    throw new Error('Choose an image from the list.');
  }

  const helpTrimmed = (helpText ?? '').trim();
  const mapping = normalizeAppMapping(appMapping);
  const sortNum = Number(sortOrder ?? 0);

  return {
    name: nameTrimmed,
    image: imageTrimmed,
    helpText: helpTrimmed,
    appMapping: mapping,
    sortOrder: Number.isFinite(sortNum) ? sortNum : 0,
  };
}

/**
 * Fetches metric definitions from `metricDefinitions` (create/update only; no delete).
 */
function useMetricDefinitionsList() {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const collRef = collection(db, METRIC_DEFINITIONS_COLLECTION);
      const snapshot = await getDocs(collRef);
      const rows = snapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));
      setMetrics(sortMetrics(rows));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load metric definitions', err);
      Sentry.captureException(err);
      setError('Failed to load metric definitions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createMetric = useCallback(
    async ({ key, name, image, helpText, appMapping, sortOrder }) => {
      const keyResult = validateMetricKey(key);
      if (!keyResult.ok) {
        throw new Error(keyResult.message);
      }
      const metricKey = keyResult.key;

      const normalized = validateAndNormalizeFields({
        name,
        image,
        helpText,
        appMapping,
        sortOrder,
      });

      const payload = {
        key: metricKey,
        ...normalized,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      try {
        setError(null);
        const ref = doc(db, METRIC_DEFINITIONS_COLLECTION, metricKey);
        const existing = await getDoc(ref);
        if (existing.exists()) {
          throw new Error(
            'A metric with this key already exists. Choose a different key.',
          );
        }
        await setDoc(ref, payload);
        const freshSnap = await getDoc(ref);
        const freshData = freshSnap.exists()
          ? { id: metricKey, ...freshSnap.data() }
          : { id: metricKey, ...payload };

        setMetrics((prev) => sortMetrics([...prev.filter((m) => m.id !== metricKey), freshData]));
        return metricKey;
      } catch (err) {
        if (err?.message && err.message.includes('already exists')) {
          throw err;
        }
        setError('Failed to create metric definition');
        throw err;
      }
    },
    [],
  );

  const updateMetric = useCallback(async (id, data) => {
    const normalized = validateAndNormalizeFields(data);

    const updates = {
      ...normalized,
      updatedAt: serverTimestamp(),
    };

    const ref = doc(db, METRIC_DEFINITIONS_COLLECTION, id);

    try {
      setError(null);
      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(ref);
        if (!snapshot.exists()) {
          throw new Error('Metric not found');
        }
        transaction.update(ref, updates);
      });

      const snap = await getDoc(ref);
      if (!snap.exists()) {
        throw new Error('Metric not found');
      }
      const fresh = { id, ...snap.data() };
      setMetrics((prev) =>
        sortMetrics(prev.map((m) => (m.id === id ? fresh : m))),
      );
    } catch (err) {
      if (err?.message !== 'Metric not found') {
        setError('Failed to update metric definition');
      }
      throw err;
    }
  }, []);

  return {
    metrics,
    loading,
    error,
    refresh,
    createMetric,
    updateMetric,
  };
}

export default useMetricDefinitionsList;
